import * as XLSX from "xlsx";
import moment from "moment";

// Sheet headers stay English regardless of the UI language -- same convention
// as skippedAccountsExport.js.
const METER_NO_HEADER = "Serial No";
const REASON_HEADER = "Reason";

const meterResultValue = (data) => data?.body?.value ?? data?.body ?? data;

/**
 * Pull the `skipped` array out of an assign/reassign meter-nos response. Accepts
 * both the bare payload and the `body.value` envelope.
 * @param {Object} data - Raw response payload.
 * @returns {Array} Entries of `{ meterNo, reason }`, or an empty array.
 */
export const getSkippedMeters = (data) => {
  const skipped = meterResultValue(data)?.skipped;
  return Array.isArray(skipped) ? skipped : [];
};

/**
 * Whether the payload carries meter-nos result counts at all, so callers can
 * tell a real (possibly all-skipped) result apart from a plain error envelope.
 * @param {Object} data - Raw response payload.
 * @returns {boolean}
 */
export const hasMeterTransferResult = (data) => {
  const value = meterResultValue(data);
  return Boolean(value) && typeof value === "object" && (
    Array.isArray(value.skipped) ||
    value.transferredCount != null ||
    value.failedCount != null ||
    value.skippedCount != null
  );
};

/**
 * Read the transferred / failed / skipped counts from a meter-nos response,
 * falling back when the backend leaves a count out.
 * @param {Object} data - Raw response payload.
 * @param {Object} fallback - `{ transferred, failed, skipped }` used per missing count.
 * @returns {{ transferred: number, failed: number, skipped: number }}
 */
export const getMeterTransferCounts = (data, fallback = {}) => {
  const value = meterResultValue(data);
  const count = (number, otherwise) => {
    const parsed = Number(number);
    return number != null && Number.isFinite(parsed) ? Math.max(0, parsed) : otherwise ?? 0;
  };
  return {
    transferred: count(value?.transferredCount, fallback.transferred),
    failed: count(value?.failedCount, fallback.failed),
    skipped: count(value?.skippedCount, fallback.skipped ?? getSkippedMeters(data).length),
  };
};

/**
 * Build a two column (meter no / reason) sheet from the skipped meters and
 * trigger an immediate download.
 * @param {Array} skippedMeters - Entries of `{ meterNo, reason }`.
 * @param {string} fileNamePrefix - Prefix for the generated .xlsx file name.
 */
export const downloadSkippedMetersExcel = (
  skippedMeters,
  fileNamePrefix = "skipped-items"
) => {
  if (!Array.isArray(skippedMeters) || skippedMeters.length === 0) return;

  // Meter numbers stay strings so leading zeroes survive in the sheet.
  const rows = skippedMeters.map((item) => ({
    [METER_NO_HEADER]: String(item?.meterNo ?? ""),
    [REASON_HEADER]: item?.reason ?? "",
  }));

  const worksheet = XLSX.utils.json_to_sheet(rows, {
    header: [METER_NO_HEADER, REASON_HEADER],
  });
  worksheet["!cols"] = [{ wch: 20 }, { wch: 60 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Skipped Meters");
  XLSX.writeFile(
    workbook,
    `${fileNamePrefix}-${moment().format("YYYY-MM-DD_HH-mm-ss")}.xlsx`
  );
};
